import { BillingClaim, BillingClaimPreview } from '../../../interfaces/billing.rpm';
import { RPMClaimStatus } from '../../../interfaces/billing';

/**
 * Type guard to check if a claim is a BillingClaim (exported) vs BillingClaimPreview (created).
 *
 * BillingClaim has:
 * - invoiceId: identifier for the exported invoice
 * - rpmNotes: associated notes with the claim
 * - invoiceCreationDate: when the invoice was created
 *
 * BillingClaimPreview has:
 * - noteId: identifier for the note
 * - startDate: creation date (instead of invoiceCreationDate)
 * - No invoiceId
 *
 * @param claim - The claim to check
 * @returns true if the claim is a BillingClaim, false if it's a BillingClaimPreview
 */
export function isBillingClaim(
  claim: BillingClaim | BillingClaimPreview
): claim is BillingClaim {
  // Check for both properties to be safe - BillingClaim should have both
  return 'invoiceId' in claim && 'rpmNotes' in claim;
}

/**
 * Exportable claim statuses (claims that can have PDF notes downloaded)
 */
const EXPORTABLE_STATUSES = [
  RPMClaimStatus.EXPORTED,
  RPMClaimStatus.EXPORTED_WITH_WARNINGS
];

/**
 * Type guard to check if a status string is a valid RPMClaimStatus enum value.
 * Provides runtime validation to catch unexpected status values from the API.
 *
 * @param status - The status string to validate
 * @returns true if the status is a valid RPMClaimStatus enum value
 */
export function isRPMClaimStatus(status: string): status is RPMClaimStatus {
  return Object.values(RPMClaimStatus).includes(status as RPMClaimStatus);
}

/**
 * Type guard to check if a claim status is exportable (can have PDF notes).
 * Only EXPORTED and EXPORTED_WITH_WARNINGS statuses can have downloadable PDF notes.
 *
 * @param status - The status string to check
 * @returns true if the status allows PDF note exports
 */
export function isExportableStatus(status: string): status is RPMClaimStatus {
  return EXPORTABLE_STATUSES.includes(status as RPMClaimStatus);
}
